<template>
  <ExcursionFilterPopupWrapper
    ref="calendarPopup"
    :filter-key="'date'"
    :title
    :static-title="staticTitle"
    :is-active="isActive"
    :is-disabled-reset-button="isDisabledResetButton"
    is-modal-only
    modal-no-padding
    :modal-class="'filter-dates-modal'"
    :class="'filter-dates'"
    @open="onPopupOpen"
    @apply="handleApply"
    @close="calendarClose"
  >
    <template v-if="isExpActive">
      <div
        id="experiment-block"
        class="experiment-block"
      >
        <ExcursionPersonStepper />
        <ExcursionFiltersFormatContent :is-experiment="isExpActive" />
      </div>
      <p class="experiment-block-date-title">
        {{ t('title') }}
      </p>
    </template>
    <div class="filter-weekdays ref-modal-fixed">
      <CalendarWeekdays />
    </div>

    <Calendar
      ref="calendarRef"
      :key="calendarKey"
      :default-selected-dates="calendarDefaultDates"
      no-weekdays
      class="filter-calendar"
      @select="onCalendarSelect"
    />

    <template #footer>
      <ExcursionPersonStepper v-if="!isExpActive" />
    </template>
  </ExcursionFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { ExcursionFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { useExcursionFiltersStore, type TExcursionFiltersState } from '@/entities/filter/store/excursion';
  import { Calendar, CalendarWeekdays } from '@experience/core-calendar-ui';
  import { useI18n } from '@experience/i18n';
  import { useForcedCalendar } from '../../composables/useForcedCalendar.ts';
  import { DEFAULT_CALENDAR_TITLE, DEFAULT_FILTER_TITLE } from '../../const/config';
  import ru from '../../i18n/ru.json';
  import { toCalendarSelectedDate } from '../../lib/localDate';
  import { getFilterTitle } from '../../lib/title';
  import ExcursionPersonStepper from './ExcursionPersonStepper.vue';
  import { computed } from 'vue';
  import { ExcursionFiltersFormatContent } from '@/features/filter/format';
  import { useExcursionCalendarFormatExperiment } from '@/features/experiments/composables/excursion/useExcursionCalendarFormatExperiment.ts';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    slug: string;
  }

  interface Emits {
    (e: 'apply', isForced: boolean, filterData?: Partial<TExcursionFiltersState>): void;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<Emits>();

  const filtersStore = useExcursionFiltersStore();
  // TODO: вынести тип наерх
  const calendarRef = ref<
    | (InstanceType<typeof Calendar> & {
        scrollToMonth: (year: number, month: number) => void;
      })
    | null
  >(null);

  const { isExpActive, setExperimentType, isExpTitle } = useExcursionCalendarFormatExperiment();

  const staticTitle = computed(() => {
    return isExpActive.value && isExpTitle.value ? t('titleExperiment') : t('title');
  });

  const calendarPopup = ref(null);
  const { calendarApply, calendarClose } = useForcedCalendar(
    calendarPopup,
    computed(() => props.slug),
  );

  const title = ref(DEFAULT_FILTER_TITLE);
  const calendarKey = ref(1);
  const defaultDates = ref<[Nullable<string>, Nullable<string>] | undefined>([
    filtersStore.searchState.date.from ?? '',
    filtersStore.searchState.date.to ?? '',
  ]);

  const calendarDefaultDates = computed(() => defaultDates.value?.map(toCalendarSelectedDate));

  const parseYearMonth = (iso: string) => {
    const [yearStr, monthStr] = iso.split('-');

    return { year: Number(yearStr), monthIndex: Number(monthStr) - 1 };
  };

  const updateScroll = () => {
    const from = filtersStore.state.date.from;
    if (!from) {
      return;
    }

    const { year, monthIndex } = parseYearMonth(from);
    const today = new Date();
    if (year === today.getFullYear() && monthIndex === today.getMonth()) {
      return;
    }
    setTimeout(() => {
      calendarRef.value?.scrollToMonth(year, monthIndex);
    }, 300);
  };

  function onCalendarSelect(from: string | null, to: string | null) {
    filtersStore.state.date.from = from;
    filtersStore.state.date.to = to;
  }

  const isDisabledResetButton = computed(() => {
    return (
      !filtersStore.state.date.from && !filtersStore.state.date.to && Number(filtersStore.state.date.persons) === 1
    );
  });

  const isActive = computed(() => title.value !== DEFAULT_CALENDAR_TITLE);

  watchEffect(() => {
    title.value = getFilterTitle(
      filtersStore.searchState.date.from,
      filtersStore.searchState.date.to,
      filtersStore.searchState.date.persons,
    );
  });

  watch(
    () => filtersStore.state.date,
    () => {
      defaultDates.value = [filtersStore.state.date.from ?? '', filtersStore.state.date.to ?? ''];
    },
  );

  const onPopupOpen = async () => {
    updateScroll();
    const isActiveFilterType = filtersStore.state.type?.length;
    if (isExpActive.value && !isActiveFilterType) {
      setExperimentType();
    }
  };

  const handleApply = (isForced: boolean) => {
    calendarApply(isForced);

    const filterData: Partial<TExcursionFiltersState> = {
      date: filtersStore.searchState.date,
    };

    if (isExpActive.value) {
      filterData.type = filtersStore.searchState.type;
    }

    emit('apply', isForced, filterData);
  };
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filter-dates {
    :deep(.infinite-calendar) {
      height: 100%;
    }

    @include adaptivity.mobile {
      &:not(.tour-dates) {
        width: 100% !important;
      }

      :deep(.ui-chip) {
        justify-content: space-between;
        width: 100%;
        max-width: 100%;
      }

      :deep(.ui-chip__label) {
        text-align: left;
      }

      :deep(.filter-popup) {
        margin-top: 32px;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }
    }
  }
</style>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filter-dates-modal {
    .ui-modal-header_fixed {
      &:after {
        display: none;
      }
    }

    .filter-weekdays {
      --cell-height: 32px;
      position: relative;
      display: flex;
      justify-content: center;
      background-color: var(--alias-color-background-default);

      &:after {
        content: ' ';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: var(--alias-color-border-default);
      }
    }

    .dates-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      max-height: none;
      padding: 8px 0 16px;
    }
  }

  .experiment-block,
  .experiment-block-date-title {
    padding: 8px 16px;
  }

  .experiment-block-date-title {
    @include typography.base-500-400-semi;
  }
</style>
